import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { failedResponse, getBackendUrl, loaded, loading, error, LoadStateInfo, notStarted, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { ConversationMessage } from "./conversationSlice";
import { RootState } from "../../app/store";
import { performMessageSend } from "./messageSendSlice";

export enum MessageType {
	TEXT				=    0,
	IMAGE				=    1,
    PDF                 =    2,
    VIDEO               =    3,
    AUDIO               =    4,
    FILE                =   99,
    SURVEY              =  700,
    SURVEY_ANSWER       =  701,
    SUGGESTION          = 1000,
	PARTY_JOINED		= 7000,
	PARTY_LEFT			= 7001,
	USER_JOINED			= 7100,
	USER_LEFT			= 7101,
    MESSAGE_DISMISSED   = 7200,
    DELETE              = 9000,
}

export const getMessageTypeName = (type: MessageType): string => {
    switch (type) {
        case MessageType.TEXT: return "Texto";
        case MessageType.IMAGE: return "Imagem";
        case MessageType.PDF: return "PDF";
        default: return "Desconhecido";
    }
};


export interface RetrieveMessageListRequest {
    conversationId: string;
}

export const retrieveMessageLatest = createAsyncThunk(
    "retrieveMessageLatest",
    async (param: RetrieveMessageListRequest, thunkAPI) => {
        console.debug("retrieve message latest");

        const response = await fetch(`${getBackendUrl()}/api/conversation/${param.conversationId}/latest`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveMessageListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.messageList.conversations[param.conversationId];
            if (status && status.latest && 
                (status.latest.state == LoadState.Loading || status.latest.state == LoadState.Loaded)
            ) {
                return false; // Do not proceed if already loading or loaded
            }
            return true; // Proceed with the request
        }
    }
);

export const retrieveMessageOld = createAsyncThunk(
    "retrieveMessageOld",
    async (param: RetrieveMessageListRequest, thunkAPI) => {
        console.debug("retrieve message old");

        const state = thunkAPI.getState() as RootState;
        const conversation = state.messageList.conversations[param.conversationId];
        let from = "0";
        if(conversation && conversation.list && conversation.list.length > 0) {
            from = conversation.list[ conversation.list.length-1 ].id;
        }
        const response = await fetch(`${getBackendUrl()}/api/conversation/${param.conversationId}/old/${from}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveMessageListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.messageList.conversations[param.conversationId];
            if (status && status.old && status.old.state == LoadState.Loading) {
                return false; // Do not proceed if already loading
            }
            return true; // Proceed with the request
        }
    }
);

export const retrieveMessageNewers = createAsyncThunk(
    "retrieveMessageNewers",
    async (param: RetrieveMessageListRequest, thunkAPI) => {
        console.debug("retrieve message newers");

        const state = thunkAPI.getState() as RootState;
        const conversation = state.messageList.conversations[param.conversationId];
        let from = 0;
        if(conversation && conversation.list && conversation.list.length > 0) {
            from = parseInt(conversation.list[ 0 ].id) - 10;
        }
        const response = await fetch(`${getBackendUrl()}/api/conversation/${param.conversationId}/messages/${from}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveMessageListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.messageList.conversations[param.conversationId];
            if (status && status.newers && status.newers.state == LoadState.Loading) {
                return false; // Do not proceed if already loading
            }
            return true; // Proceed with the request
        }
    }
);

export interface MessageListState {
    latest: LoadStateInfo;
    old: LoadStateInfo;
    moreOld: boolean;
    newers: LoadStateInfo;
    list?: ConversationMessage[];
}

export interface ConversationMessagesState {
    conversations: Record<string, MessageListState>;
}

const initialState: ConversationMessagesState = {
    conversations: {},
};

export const messageListSlice = createSlice({
    name: "messageList",
    initialState,
    reducers: {
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveMessageLatest.pending, (state, action) => {
                if(! state.conversations[action.meta.arg.conversationId]) {
                    state.conversations[action.meta.arg.conversationId] = {
                        latest: notStarted(),
                        old: notStarted(),
                        newers: notStarted(),
                        list: [],
                        moreOld: true,
                    };
                }
                state.conversations[action.meta.arg.conversationId].latest = loading();
            })
            .addCase(retrieveMessageLatest.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.conversations[action.meta.arg.conversationId].latest = loaded();
                state.conversations[action.meta.arg.conversationId].list = action.payload.reverse();
            })
            .addCase(retrieveMessageLatest.rejected, (state, action) => {
                state.conversations[action.meta.arg.conversationId].latest = error(action);
            })


            .addCase(retrieveMessageNewers.pending, (state, action) => {
                if(! state.conversations[action.meta.arg.conversationId]) {
                    state.conversations[action.meta.arg.conversationId] = {
                        latest: notStarted(),
                        old: notStarted(),
                        newers: notStarted(),
                        list: [],
                        moreOld: true,
                    };
                }
                state.conversations[action.meta.arg.conversationId].newers = loading();
            })
            .addCase(retrieveMessageNewers.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.conversations[action.meta.arg.conversationId].newers = loaded();
                const list = state.conversations[action.meta.arg.conversationId].list;
                if(list) {
                    for(const msg of action.payload) {
                        if(list.length > 0 && parseInt(msg.id) <= parseInt(list[0].id)) {
                            continue; // Skip messages that are already in the list
                        }
                        list.unshift(msg);
                    }
                }
            })
            .addCase(retrieveMessageNewers.rejected, (state, action) => {
                state.conversations[action.meta.arg.conversationId].newers = error(action);
            })


            .addCase(retrieveMessageOld.pending, (state, action) => {
                state.conversations[action.meta.arg.conversationId].old = loading();
            })
            .addCase(retrieveMessageOld.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.conversations[action.meta.arg.conversationId].old = loaded();
                const list = state.conversations[action.meta.arg.conversationId].list;
                if(list) {
                    for(const msg of action.payload.reverse()) {
                        list.push(msg);
                    }
                }
                if(action.payload.length == 0) {
                    state.conversations[action.meta.arg.conversationId].moreOld = false;
                }
            })
            .addCase(retrieveMessageOld.rejected, (state, action) => {
                state.conversations[action.meta.arg.conversationId].old = error(action);
            })
            
            // from other slices
            .addCase(performMessageSend.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.conversations[action.meta.arg.conversationId].latest = loaded();
                if (!state.conversations[action.meta.arg.conversationId].list) {
                    state.conversations[action.meta.arg.conversationId].list = [];
                }
                state.conversations[action.meta.arg.conversationId]?.list?.unshift(action.payload);
            });
    },
});

// export const { } = messageListSlice.actions;

export const selectMessageList = (state: RootState) => state.messageList;

export default messageListSlice.reducer;
